import { getStoredConsent } from './cookies';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    GA_INITIALIZED?: boolean;
  }
}

// Use a GA4 measurement ID (G-XXXXXXXXXX) supplied at build time. The former
// Universal Analytics ID is intentionally no longer used because that service
// no longer processes new data.
const GA_TRACKING_ID = process.env.GATSBY_GA_MEASUREMENT_ID || '';

/**
 * Check if user has Do Not Track enabled
 */
export const hasDoNotTrack = (): boolean => {
  if (typeof window === 'undefined') return false;

  const dnt = navigator.doNotTrack || (window as any).doNotTrack || (navigator as any).msDoNotTrack;
  return dnt === '1' || dnt === 'yes';
};

/**
 * Initialize Google Analytics only if user has consented
 */
export const initializeAnalytics = (): void => {
  if (typeof window === 'undefined') return;

  // Skip if already initialized
  if (window.GA_INITIALIZED) return;

  if (!GA_TRACKING_ID) {
    console.log('Analytics not configured: set GATSBY_GA_MEASUREMENT_ID');
    return;
  }

  // Check for Do Not Track
  if (hasDoNotTrack()) {
    console.log('Analytics blocked: Do Not Track is enabled');
    return;
  }

  // Check for user consent
  const consent = getStoredConsent();

  // Only initialize if user has explicitly consented to analytics
  if (!consent || !consent.analytics) {
    console.log('Analytics blocked: No user consent');
    blockGoogleAnalytics();
    return;
  }

  // Load Google Analytics
  loadGoogleAnalytics();
};

/**
 * Dynamically load Google Analytics scripts
 */
const loadGoogleAnalytics = (): void => {
  if (typeof window === 'undefined' || window.GA_INITIALIZED) return;

  // Create dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer!.push(arguments);
  };

  // Set default consent state to granted (since we already checked)
  window.gtag('consent', 'default', {
    'analytics_storage': 'granted',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied'
  });

  // Configure GA
  window.gtag('js', new Date());
  window.gtag('config', GA_TRACKING_ID, {
    page_path: window.location.pathname,
    anonymize_ip: true,
    cookie_expires: 63072000, // 2 years
    cookie_flags: 'SameSite=None;Secure'
  });

  // Load the external script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  script.onload = () => {
    window.GA_INITIALIZED = true;
    console.log('Google Analytics initialized with consent');
  };
  script.onerror = () => {
    console.error('Failed to load Google Analytics');
  };

  // Only append if not already present
  if (!document.querySelector(`script[src*="googletagmanager.com"]`)) {
    document.head.appendChild(script);
  }
};

/**
 * Block Google Analytics and clean up
 */
const blockGoogleAnalytics = (): void => {
  if (typeof window === 'undefined') return;

  // Remove any GA scripts
  const gaScripts = document.querySelectorAll('script[src*="google-analytics.com"], script[src*="googletagmanager.com"]');
  gaScripts.forEach(script => script.remove());

  // Clear dataLayer
  window.dataLayer = [];

  // Override gtag to prevent tracking
  window.gtag = function() {
    console.log('Analytics blocked: gtag call prevented');
  };

  // Delete GA cookies
  deleteGoogleAnalyticsCookies();
};

/**
 * Delete all Google Analytics related cookies
 */
export const deleteGoogleAnalyticsCookies = (): void => {
  if (typeof window === 'undefined') return;

  const cookiesToDelete = [
    '_ga',
    '_gid',
    '_gat',
    `_gat_gtag_${GA_TRACKING_ID.replace(/-/g, '_')}`,
    '_gac_',
    '__utma',
    '__utmb',
    '__utmc',
    '__utmt',
    '__utmz'
  ];

  const hostname = window.location.hostname;
  const domains = [
    hostname,
    `.${hostname}`,
    hostname.replace(/^www\./, ''),
    `.${hostname.replace(/^www\./, '')}`
  ];

  cookiesToDelete.forEach(cookieName => {
    // Try deleting for all possible domain variations
    domains.forEach(domain => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    });
  });

  // Also check for cookies with GA property ID
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('_ga') || name.startsWith('_gid') || name.includes('_gac')) {
      domains.forEach(domain => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      });
    }
  });
};

/**
 * Track page views (only if analytics is enabled)
 */
export const trackPageView = (path: string): void => {
  if (typeof window === 'undefined' || !window.gtag || !window.GA_INITIALIZED) return;

  const consent = getStoredConsent();
  if (!consent || !consent.analytics) return;

  window.gtag('config', GA_TRACKING_ID, {
    page_path: path
  });
};

/**
 * Track custom events (only if analytics is enabled)
 */
export const trackEvent = (action: string, category: string, label?: string, value?: number): void => {
  if (typeof window === 'undefined' || !window.gtag || !window.GA_INITIALIZED) return;

  const consent = getStoredConsent();
  if (!consent || !consent.analytics) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value
  });
};

/**
 * Handle consent changes
 */
export const handleConsentChange = (newConsent: any): void => {
  if (typeof window === 'undefined') return;

  if (newConsent.analytics && !window.GA_INITIALIZED) {
    // User just gave consent, initialize analytics
    loadGoogleAnalytics();
  } else if (!newConsent.analytics && window.GA_INITIALIZED) {
    // User revoked consent, block analytics
    blockGoogleAnalytics();
    window.GA_INITIALIZED = false;
  }
};

/**
 * Check if analytics is active
 */
export const isAnalyticsActive = (): boolean => {
  if (typeof window === 'undefined') return false;

  const consent = getStoredConsent();
  return !!(consent?.analytics && window.GA_INITIALIZED && !hasDoNotTrack());
};
